// 達成度判断表の判定（assessment: OK / WARNING / NG / null）の解決・色・説明。
// 画面（Matrix）、Excel 出力、計算画面の説明文が同じ定義を参照するため共通層に置く。

export type AssessmentColorKey = 'ok' | 'warning' | 'ng' | 'none' | 'other';

// BE は大文字（"OK" / "WARNING" / "NG"）で返す。表記ゆれに備えて大文字小文字・前後空白を吸収する。
// 未計算（null）は none、想定外の値は other（色なし）にして、新しい値が来ても描画を壊さない。
export const resolveAssessmentKey = (value: string | null | undefined): AssessmentColorKey => {
    if (value === null || value === undefined) return 'none';
    const normalized = `${value}`.trim().toLowerCase();
    if (normalized === 'ok') return 'ok';
    if (normalized === 'warning') return 'warning';
    if (normalized === 'ng') return 'ng';
    if (normalized === 'none') return 'none';
    return 'other';
};

// 6 桁 hex（# なし）。画面は `#${hex}`、Excel は `FF${hex}`（ARGB）で使い、色の定義を 1 箇所にする。
// OK=青 / WARNING=黄 / NG=赤 / 未計算=グレー。other は色なし。
export const ASSESSMENT_FILL_HEX: Record<AssessmentColorKey, string | null> = {
    ok: 'B3E5FC',
    warning: 'FFF59D',
    ng: 'FFCDD2',
    none: 'E0E0E0',
    other: null,
};

export const assessmentCssColor = (key: AssessmentColorKey): string => {
    const hex = ASSESSMENT_FILL_HEX[key];
    return hex ? `#${hex}` : 'transparent';
};

export const assessmentExcelArgb = (key: AssessmentColorKey): string | undefined => {
    const hex = ASSESSMENT_FILL_HEX[key];
    return hex ? `FF${hex}` : undefined;
};

// WARNING は「目標は達成しているが余裕が少ない」状態。判断表の凡例と計算画面の説明で同じ文言を使う。
// 判定式は −マージン < 偏差 ≤ 0（余裕がマージン未満）で、OK（偏差 ≤ −マージン＝余裕がマージン以上）と境界が重ならない。
export const WARNING_SUMMARY = '目標は達成しているが、余裕がマージン未満（ギリギリクリア）の状態';

export type AssessmentLegendItem = {
    key: AssessmentColorKey;
    label: string;
    description: string;
};

export const ASSESSMENT_LEGEND: readonly AssessmentLegendItem[] = [
    { key: 'ok', label: 'OK', description: '目標を達成し、余裕がマージン以上ある状態' },
    { key: 'warning', label: 'WARNING', description: WARNING_SUMMARY },
    { key: 'ng', label: 'NG', description: '目標未達の状態' },
];

// deviation_max = 周波数範囲内での「補正後設計値 − 目標値」の最大値（Epic #48 の用語表）。
// 色は BE が丸め前の値で判定した結果で、セルの表示だけが小数 1 桁。境界付近で式と食い違って見えないよう注記する。
export const ASSESSMENT_RULE_NOTE =
    'セルの値は「補正後設計値 − 目標値」の最大値（dB）で、小数 1 桁に丸めて表示します（判定は丸め前の値）。偏差 > 0 → NG ／ −マージン < 偏差 ≤ 0 → WARNING ／ 偏差 ≤ −マージン → OK';
